/**
 * References Library — Admin CRUD page.
 *
 * Browse / search / filter / edit / merge / deprecate the shared references
 * store. Every citation surfaces the pages currently using it; merges and
 * deprecations rewrite consuming pages via the backend before the change is
 * finalized. `used_by_count` is derived on the server — this UI never edits
 * it directly.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Filter, ChevronDown, ChevronRight, Edit3, Save, X,
  AlertTriangle, Archive, Undo2, GitMerge, ExternalLink, RefreshCw,
} from "lucide-react";
import api from "../../lib/api";

const STATUS_OPTS = [
  { k: "", label: "Any verification" },
  { k: "verified_pubmed", label: "PubMed-verified" },
  { k: "verified_domain", label: "Authoritative domain" },
  { k: "unverified_pending_review", label: "Unverified" },
];

const STATUS_BADGE = {
  verified_pubmed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  verified_domain: "bg-sky-50 text-sky-800 border-sky-200",
  unverified_pending_review: "bg-amber-50 text-amber-800 border-amber-200",
};

const emptyForm = {
  pmid: "", doi: "", title: "", authors: "", journal: "",
  year: "", url: "", verification_status: "unverified_pending_review",
  reviewed_by: "", note: "",
};

export default function ReferencesLibrary() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUsage = searchParams.get("usage") || "";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [journal, setJournal] = useState("");
  const [year, setYear] = useState("");
  const [deprecated, setDeprecated] = useState("false");
  const [usage, setUsage] = useState(initialUsage);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState(emptyForm);
  const [expandedUsage, setExpandedUsage] = useState({});
  const [mergeMode, setMergeMode] = useState(null); // { primary: refObj }

  // Keep the URL query in sync with the usage filter so shareable links
  // (from the readiness dashboard "Review orphans" link) work both ways.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (usage) next.set("usage", usage); else next.delete("usage");
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usage]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q, status, journal, year, deprecated, limit: 100 };
      if (usage) params.usage = usage;
      const { data } = await api.get("/admin/references", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [q, status, journal, year, deprecated, usage]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (r) => {
    setEditingId(r.id);
    setEditForm({
      pmid: r.pmid || "", doi: r.doi || "", title: r.title || "",
      authors: (r.authors || []).join(", "),
      journal: r.journal || "", year: r.year || "", url: r.url || "",
      verification_status: r.verification_status || "unverified_pending_review",
      reviewed_by: r.reviewed_by || "", note: r.note || "",
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/references/${editingId}`, {
        ...editForm,
        authors: editForm.authors.split(",").map((a) => a.trim()).filter(Boolean),
      });
      toast.success("Saved");
      setEditingId(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const createRef = async () => {
    try {
      await api.post("/admin/references", {
        ...newForm,
        authors: newForm.authors.split(",").map((a) => a.trim()).filter(Boolean),
      });
      toast.success("Reference created");
      setCreating(false);
      setNewForm(emptyForm);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const doDeprecate = async (r) => {
    const reason = window.prompt(`Deprecate "${r.title.slice(0, 60)}"?\nReason:`, "");
    if (!reason) return;
    try {
      await api.post(`/admin/references/${r.id}/deprecate`, { reason });
      toast.success("Deprecated");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doRestore = async (r) => {
    try {
      await api.post(`/admin/references/${r.id}/restore`);
      toast.success("Restored");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startMerge = (r) => setMergeMode({ primary: r });
  const cancelMerge = () => setMergeMode(null);

  const doMerge = async (dup) => {
    if (!mergeMode) return;
    const reason = window.prompt(
      `Merge "${dup.title.slice(0, 40)}" INTO "${mergeMode.primary.title.slice(0, 40)}"?\nReason:`, "");
    if (!reason && reason !== "") return;
    try {
      const { data } = await api.post("/admin/references/merge", {
        primary_id: mergeMode.primary.id,
        duplicate_id: dup.id,
        reason,
      });
      toast.success(`Merged — ${data.result.pages_updated} page(s) updated`);
      setMergeMode(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Merge failed"); }
  };

  const doRecount = async () => {
    try {
      const { data } = await api.post("/admin/references/recount");
      toast.success(`Recounted ${data.refs_touched} refs — ${data.orphan_ref_ids_in_pages} orphaned`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Recount failed"); }
  };

  const toggleUsage = async (rid) => {
    if (expandedUsage[rid]) {
      const c = { ...expandedUsage };
      delete c[rid];
      setExpandedUsage(c);
      return;
    }
    try {
      const { data } = await api.get(`/admin/references/${rid}`);
      setExpandedUsage((prev) => ({ ...prev, [rid]: data.used_by_pages || [] }));
    } catch (e) { toast.error("Failed to fetch usage"); }
  };

  const filtered = useMemo(() => rows, [rows]);

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="vv-container py-8" data-testid="references-library-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl text-stone-900">References Library</h1>
            <p className="text-sm text-stone-600 mt-1">Shared, de-duplicated citation store used by every content page.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="text-sm inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-4 py-2 hover:border-stone-300"
              onClick={doRecount}
              data-testid="refs-recount"
            >
              <RefreshCw size={14} /> Recount usage
            </button>
            <button
              type="button"
              className="text-sm inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 font-semibold"
              onClick={() => setCreating(true)}
              data-testid="refs-new"
            >
              + New reference
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="vv-card p-4 grid md:grid-cols-[1fr_180px_140px_100px_140px] gap-3 mb-6">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title, journal, PMID, DOI, author…"
              className="w-full bg-white border border-stone-200 rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-[color:var(--vv-teal)]"
              data-testid="refs-search"
            />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-white border border-stone-200 rounded-full px-4 py-2 text-sm" data-testid="refs-status-filter">
            {STATUS_OPTS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
          </select>
          <input value={journal} onChange={(e) => setJournal(e.target.value)} placeholder="Journal" className="bg-white border border-stone-200 rounded-full px-4 py-2 text-sm" data-testid="refs-journal-filter" />
          <input value={year} onChange={(e) => setYear(e.target.value)} placeholder="Year" className="bg-white border border-stone-200 rounded-full px-4 py-2 text-sm" data-testid="refs-year-filter" />
          <select value={deprecated} onChange={(e) => setDeprecated(e.target.value)} className="bg-white border border-stone-200 rounded-full px-4 py-2 text-sm" data-testid="refs-deprecated-filter">
            <option value="false">Active only</option>
            <option value="true">Deprecated only</option>
            <option value="all">All</option>
          </select>
        </div>

        <div className="text-xs text-stone-500 mb-2 flex items-center justify-between flex-wrap gap-2">
          <span data-testid="refs-count">
            Showing {filtered.length} of {total} references
            {usage === "orphaned" ? " · Orphaned only (used_by_count = 0)" : ""}
            {loading ? " · Loading…" : ""}
          </span>
          <div className="flex items-center gap-2">
            {usage === "orphaned" ? (
              <button
                type="button"
                className="text-[11px] uppercase tracking-widest inline-flex items-center gap-1 bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-2.5 py-1 hover:bg-amber-200"
                onClick={() => setUsage("")}
                data-testid="refs-usage-orphan-clear"
              >
                Orphaned filter <X size={12} />
              </button>
            ) : null}
            {mergeMode ? (
              <span className="inline-flex items-center gap-2 text-amber-800">
                <GitMerge size={12} /> Merge mode — select the duplicate to fold into <b className="ml-1">{mergeMode.primary.title.slice(0, 40)}</b>
                <button type="button" className="underline hover:text-stone-800 ml-2" onClick={cancelMerge}>Cancel</button>
              </span>
            ) : null}
          </div>
        </div>

        {/* New form */}
        {creating ? (
          <div className="vv-card p-4 mb-4" data-testid="refs-new-form">
            <FormFields form={newForm} onChange={setNewForm} />
            <div className="flex justify-end gap-2 mt-3">
              <button type="button" className="text-sm text-stone-600" onClick={() => { setCreating(false); setNewForm(emptyForm); }}>Cancel</button>
              <button type="button" className="text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-full px-4 py-1.5 font-semibold" onClick={createRef} data-testid="refs-new-submit">Create</button>
            </div>
          </div>
        ) : null}

        {/* Table */}
        <div className="space-y-2">
          {filtered.map((r) => (
            <article
              key={r.id}
              data-testid={`refs-row-${r.id}`}
              data-deprecated={r.deprecated ? "true" : "false"}
              className={`vv-card p-4 ${r.deprecated ? "opacity-70" : ""} ${mergeMode?.primary?.id === r.id ? "ring-2 ring-amber-400" : ""}`}
            >
              {editingId === r.id ? (
                <div>
                  <FormFields form={editForm} onChange={setEditForm} />
                  <div className="flex justify-end gap-2 mt-3">
                    <button type="button" className="text-sm text-stone-600" onClick={() => setEditingId(null)}>Cancel</button>
                    <button type="button" className="text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-full px-4 py-1.5 font-semibold" onClick={saveEdit} data-testid={`refs-save-${r.id}`}>
                      <Save size={12} className="inline mr-1" /> Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <button type="button" className="text-stone-500 mt-1" onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                    {expandedId === r.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-stone-900 leading-snug">{r.title}</div>
                        <div className="text-xs text-stone-500 mt-1 flex items-center gap-2 flex-wrap">
                          {r.journal ? <span>{r.journal}{r.year ? `, ${r.year}` : ""}</span> : null}
                          {r.pmid ? <span>PMID: {r.pmid}</span> : null}
                          {r.doi ? <span>DOI: {r.doi}</span> : null}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest ${STATUS_BADGE[r.verification_status] || "bg-stone-50 text-stone-700 border-stone-200"}`}>
                            {r.verification_status?.replace(/_/g, " ")}
                          </span>
                          {r.deprecated ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest bg-rose-50 text-rose-800 border-rose-200"><AlertTriangle size={10} /> Deprecated</span> : null}
                          <button type="button" className="underline text-stone-600 hover:text-stone-800" onClick={() => toggleUsage(r.id)} data-testid={`refs-usage-toggle-${r.id}`}>
                            Used by {r.used_by_count ?? 0}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {mergeMode ? (
                          mergeMode.primary.id === r.id ? (
                            <span className="text-xs text-amber-800 font-semibold">PRIMARY</span>
                          ) : (
                            <button type="button" className="text-xs bg-amber-500 hover:bg-amber-600 text-white rounded-full px-3 py-1" onClick={() => doMerge(r)} data-testid={`refs-merge-into-${r.id}`}>
                              Merge into primary
                            </button>
                          )
                        ) : (
                          <>
                            {r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-stone-500 hover:text-stone-800 p-1" aria-label="Open source"><ExternalLink size={14} /></a> : null}
                            <button type="button" className="text-stone-500 hover:text-stone-800 p-1" onClick={() => openEdit(r)} data-testid={`refs-edit-${r.id}`} aria-label="Edit">
                              <Edit3 size={14} />
                            </button>
                            <button type="button" className="text-stone-500 hover:text-stone-800 p-1" onClick={() => startMerge(r)} data-testid={`refs-merge-${r.id}`} aria-label="Start merge">
                              <GitMerge size={14} />
                            </button>
                            {r.deprecated ? (
                              <button type="button" className="text-stone-500 hover:text-emerald-700 p-1" onClick={() => doRestore(r)} data-testid={`refs-restore-${r.id}`} aria-label="Restore">
                                <Undo2 size={14} />
                              </button>
                            ) : (
                              <button type="button" className="text-stone-500 hover:text-rose-700 p-1" onClick={() => doDeprecate(r)} data-testid={`refs-deprecate-${r.id}`} aria-label="Deprecate">
                                <Archive size={14} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {expandedId === r.id ? (
                      <div className="mt-3 text-xs text-stone-600 border-t border-stone-100 pt-3 space-y-2">
                        {r.authors?.length ? <div><b>Authors:</b> {r.authors.join(", ")}</div> : null}
                        {r.url ? <div className="truncate"><b>URL:</b> <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-[color:var(--vv-teal)] hover:underline">{r.url}</a></div> : null}
                        {r.alias_urls?.length ? <div className="text-stone-500"><b>Alias URLs:</b> {r.alias_urls.length}</div> : null}
                        {r.alias_titles?.length ? <div className="text-stone-500"><b>Alias titles:</b> {r.alias_titles.slice(0, 2).join(" · ")}</div> : null}
                        {r.note ? <div><b>Note:</b> {r.note}</div> : null}
                        {r.reviewed_by ? <div><b>Reviewed by:</b> {r.reviewed_by}</div> : null}
                        {r.deprecated_reason ? <div><b>Deprecated:</b> {r.deprecated_reason}</div> : null}
                        {r.history?.length ? (
                          <details>
                            <summary className="cursor-pointer text-stone-500">History ({r.history.length})</summary>
                            <ul className="mt-1 pl-4 border-l border-stone-100">
                              {r.history.map((h, i) => (
                                <li key={`${h.at || ""}-${h.action || ""}-${i}`} className="text-[11px] text-stone-500">
                                  {h.action} · {h.actor} · {h.at}
                                </li>
                              ))}
                            </ul>
                          </details>
                        ) : null}
                      </div>
                    ) : null}

                    {expandedUsage[r.id] ? (
                      <div className="mt-3 text-xs border-t border-stone-100 pt-3" data-testid={`refs-usage-list-${r.id}`}>
                        <div className="text-stone-500 mb-1">Used on:</div>
                        <ul className="space-y-1">
                          {expandedUsage[r.id].length === 0 ? (
                            <li className="text-stone-500 italic">No pages currently reference this record.</li>
                          ) : expandedUsage[r.id].map((p, i) => (
                            <li key={`${p.page_type}-${p.slug}-${i}`}>
                              <a className="text-[color:var(--vv-teal)] hover:underline" href={`/admin/preview/${p.page_type}/${p.slug}`}>
                                {p.page_type}/{p.slug}
                              </a>
                              {p.status ? <span className="text-stone-400 ml-2">· {p.status}</span> : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </article>
          ))}
          {!loading && filtered.length === 0 ? (
            <div className="vv-card p-8 text-center text-stone-500" data-testid="refs-empty">
              No references match those filters.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function FormFields({ form, onChange }) {
  const set = (k) => (e) => onChange({ ...form, [k]: e.target.value });
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
      <label className="col-span-2 md:col-span-4">
        <span className="text-xs uppercase tracking-widest text-stone-500">Title *</span>
        <input value={form.title} onChange={set("title")} className="w-full bg-white border border-stone-200 rounded px-2 py-1.5 mt-0.5" data-testid="refs-form-title" />
      </label>
      <label>
        <span className="text-xs uppercase tracking-widest text-stone-500">PMID</span>
        <input value={form.pmid} onChange={set("pmid")} className="w-full bg-white border border-stone-200 rounded px-2 py-1.5 mt-0.5" data-testid="refs-form-pmid" />
      </label>
      <label>
        <span className="text-xs uppercase tracking-widest text-stone-500">DOI</span>
        <input value={form.doi} onChange={set("doi")} className="w-full bg-white border border-stone-200 rounded px-2 py-1.5 mt-0.5" data-testid="refs-form-doi" />
      </label>
      <label className="col-span-2">
        <span className="text-xs uppercase tracking-widest text-stone-500">URL</span>
        <input value={form.url} onChange={set("url")} className="w-full bg-white border border-stone-200 rounded px-2 py-1.5 mt-0.5" data-testid="refs-form-url" />
      </label>
      <label className="col-span-2">
        <span className="text-xs uppercase tracking-widest text-stone-500">Journal</span>
        <input value={form.journal} onChange={set("journal")} className="w-full bg-white border border-stone-200 rounded px-2 py-1.5 mt-0.5" data-testid="refs-form-journal" />
      </label>
      <label>
        <span className="text-xs uppercase tracking-widest text-stone-500">Year</span>
        <input value={form.year} onChange={set("year")} className="w-full bg-white border border-stone-200 rounded px-2 py-1.5 mt-0.5" data-testid="refs-form-year" />
      </label>
      <label>
        <span className="text-xs uppercase tracking-widest text-stone-500">Verification</span>
        <select value={form.verification_status} onChange={set("verification_status")} className="w-full bg-white border border-stone-200 rounded px-2 py-1.5 mt-0.5">
          <option value="verified_pubmed">PubMed-verified</option>
          <option value="verified_domain">Authoritative domain</option>
          <option value="unverified_pending_review">Unverified</option>
        </select>
      </label>
      <label className="col-span-2 md:col-span-4">
        <span className="text-xs uppercase tracking-widest text-stone-500">Authors (comma-separated)</span>
        <input value={form.authors} onChange={set("authors")} className="w-full bg-white border border-stone-200 rounded px-2 py-1.5 mt-0.5" data-testid="refs-form-authors" />
      </label>
      <label className="col-span-2 md:col-span-4">
        <span className="text-xs uppercase tracking-widest text-stone-500">Note</span>
        <input value={form.note} onChange={set("note")} className="w-full bg-white border border-stone-200 rounded px-2 py-1.5 mt-0.5" data-testid="refs-form-note" />
      </label>
    </div>
  );
}
